import React, { useContext, useState, useEffect, forwardRef, useImperativeHandle, useCallback } from "react";
import { CardContext, SlotMachineContext } from "../../../context";
import whiteArrow from "../../../assets/icons/white_arrow.svg";
import { FrontSide, MainButton } from "../../atoms";
import { ICard } from "../../../interfaces";
import { useWindowDimensions, useSound } from "../../../hooks";
import { CardType } from "../../../util/enums";
import PdfIndex from "../../molecules/Statistics/Pdf/PdfIndex";
import CreateUserInputGeneric from "../../molecules/LogIn/CreateUserMolecule/CreateUserInputGeneric";
import { validateEmail } from "../../molecules/LogIn/helpers";
import { useTranslation } from "react-i18next";
import axios from "axios";

export interface SlotMachineRefObject {
  dropedStrenghtCard(): void;
}

const SlotMachineChosenCardHolder = forwardRef((_props: {}, ref: React.Ref<SlotMachineRefObject>) => {
  useImperativeHandle(ref, () => ({ dropedStrenghtCard }));

  const { t } = useTranslation();
  const { draggableCard, setIsStrengthCardCurrentlyDragged } = useContext(CardContext);
  const {
    setConfirmSelectionDisabled,
    setLastDroppedCard,
    isConfirmSelectionClicked,
    verticalCardIndexOfSelectedRow,
    verticalCardIdsAndTheirIndexes,
    setAddStrengthCardDemo,
    generateFiveNewStrengthCardsManually,
    setGenerateFiveNewStrengthCardsManually,
  } = useContext(SlotMachineContext);

  const [chosenStrengthCard, setChosenStrengthCard] = useState<(ICard | undefined)[]>(new Array(5));
  const { width } = useWindowDimensions();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const { playSound } = useSound();
  const demoCard: ICard = {
    type: CardType.STRENGTH,
    description: "0",
    id: 1111,
  };
  const [userEmail, setUserEmail] = useState<string>("");
  const [emailInputMessage, setEmailInputMessage] = useState<string>("");
  const emailHandler: Function = (value: string) => {
    if (!validateEmail(userEmail)) {
      setEmailInputMessage(t("yourEmailAddressIsNotValiedText"));
      return false;
    }

    if (userEmail.length > 50) {
      setEmailInputMessage(t("thisEmailIsTooLongText"));
      return false;
    }

    setEmailInputMessage("");
    return true;
  };

  const handleOnChange: Function = (fieldName: string, value: string) => {
    if (fieldName === "email") setUserEmail(value);
    emailHandler();
  };

  const simulateGenerationOfNewStrengthCards = () => {
    setGenerateFiveNewStrengthCardsManually(!generateFiveNewStrengthCardsManually);
  };

  const numberOfPopulatedCards = useCallback(() => {
    return chosenStrengthCard.filter((card) => card !== undefined).length;
  }, [chosenStrengthCard]);

  useEffect(() => {
    if (numberOfPopulatedCards() === 5 && !isConfirmSelectionClicked && userEmail != "" && emailInputMessage == "")
      return setConfirmSelectionDisabled(false);

    return setConfirmSelectionDisabled(true);
  }, [chosenStrengthCard, setConfirmSelectionDisabled, userEmail, emailInputMessage, numberOfPopulatedCards, isConfirmSelectionClicked]);

  const isCardChoosen = (draggableCard: any) => {
    return chosenStrengthCard.filter((card) => {
      if (card) return card.description === draggableCard.description;
      return false;
    }).length;
  };

  const dropedStrenghtCard = () => {
    if (selectedIndex === null) return;

    if (isCardChoosen(draggableCard)) {
      return;
    }

    if (draggableCard) {
      if (numberOfPopulatedCards() < 5) {
        simulateGenerationOfNewStrengthCards();

        const copyOfChoosen: ICard[] = [];
        const choosenCards = Object.assign(copyOfChoosen, chosenStrengthCard);

        if (choosenCards[selectedIndex]) return;

        choosenCards[selectedIndex] = draggableCard;

        setChosenStrengthCard(choosenCards);
        setAddStrengthCardDemo({ id: draggableCard?.id || 1111, description: draggableCard?.description || "" });

        verticalCardIndexOfSelectedRow.current = verticalCardIdsAndTheirIndexes?.find((card: any) => draggableCard.id === card.cardId)?.verticalIndex;
        setLastDroppedCard(draggableCard);
      }
    }
    setIsStrengthCardCurrentlyDragged(false);
  };

  const removeChosenCard = (index: number) => {
    const copyOfChoosen: (ICard | undefined)[] = [];
    const choosenCards = Object.assign(copyOfChoosen, chosenStrengthCard);

    setSelectedIndex(null);
    choosenCards[index] = undefined;
    setChosenStrengthCard(choosenCards);
    playSound("/sounds/CloseCard.mp3");

    return setConfirmSelectionDisabled(true);
  };

  const draggedStrengthCardsFields = (index: number) => {
    return (
      <div
        onMouseEnter={() => setSelectedIndex(index)}
        onMouseLeave={() => setSelectedIndex(null)}
        className="drop_container__content center_horizontally_and_vertically"
        style={{ minHeight: width * 0.155, minWidth: width * 0.118 }}>
        {chosenStrengthCard[index] && !isConfirmSelectionClicked && (
          <div className="slot_machine_lower_part_clear_x" onClick={() => removeChosenCard(index)}>
            {" "}
          </div>
        )}
        {chosenStrengthCard[index] && <FrontSide cardData={chosenStrengthCard[index] || demoCard} />}
      </div>
    );
  };

  const SlotMachineChosenCards = () => {
    return Array.apply(null, Array(5)).map((_item: any, index: number) => {
      return (
        <div key={index} style={{ marginRight: "5%" }}>
          <div style={{ position: "relative" }}>
            {" "}
            <div className="drop_containter_parent" style={{ minHeight: width * 0.156, minWidth: width * 0.12 }}></div>{" "}
          </div>
          <div className="slot_machine_placeholder_border">{draggedStrengthCardsFields(index)}</div>
        </div>
      );
    });
  };

  const downloadClick = () => {
    const emailCheck = emailHandler();
    if (emailCheck && userEmail.length > 0) {
      axios
        .post(process.env.REACT_APP_ADMIN_BACKEND_URL + "api/questionary", {
          email: userEmail,
          cards: chosenStrengthCard,
        })
        .catch((err) => console.error(err));
    } else return false;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <div className="slot_machine_lower_part_wrapper">
        <div className="slot_machine_lower_horizontal_line">
          <div className="linear_gradient_line_horizontal"> </div>
        </div>

        <div style={{ width: "100%" }}>
          <div className="drop_container " style={{ marginLeft: "5%" }}>
            {SlotMachineChosenCards()}
          </div>
        </div>
      </div>

      <div style={{ width: "21%", alignSelf: "center" }}>
        <CreateUserInputGeneric
          type="email"
          message={emailInputMessage}
          value={userEmail}
          handleOnChange={(type: string, value: any) => handleOnChange(type, value)}
          validateInputs={() => emailHandler()}
        />
      </div>
      {(numberOfPopulatedCards() < 5 || userEmail == "" || emailInputMessage != "") && (
        <div style={{ marginTop: width * 0.01, width: "15%", marginBottom: width * 0.03 }} className="slot_machine__confirm_button">
          <MainButton type="big" text="Download" clickHandler={() => console.log()} disabled={true} />
          <div className="white_arrow_confirm_button_in_slot_machine_demo_data_wrapper">
            <img className="white_arrow_in_slot_machine" src={whiteArrow} alt="white arrow" />
          </div>
        </div>
      )}

      {numberOfPopulatedCards() > 4 && emailInputMessage == "" && userEmail != "" && (
        <div
          style={{ marginTop: width * 0.01, width: "20%", marginBottom: width * 0.03, justifyContent: "center" }}
          className="slot_machine__confirm_button">
          <PdfIndex
            sendEmailForDemoSlot={downloadClick}
            pdfType="strengthsDemo"
            selectedStrengthsDemo={[
              chosenStrengthCard[0] || "",
              chosenStrengthCard[1] || "",
              chosenStrengthCard[2] || "",
              chosenStrengthCard[3] || "",
              chosenStrengthCard[4] || "",
            ]}
          />
          <div className="white_arrow_confirm_button_in_slot_machine_demo_data_wrapper">
            <img className="white_arrow_in_slot_machine" src={whiteArrow} alt="white arrow" />
          </div>
        </div>
      )}
    </div>
  );
});

export default SlotMachineChosenCardHolder;
